"use client";

import { storePendingPurchase, type PendingPurchase } from "@/lib/ga4-pending-purchase";
import { trackAddToCart, trackBeginCheckout } from "@/lib/ga4-client";
import { AnalyticsEvents, trackEvent } from "@/lib/analytics/index";
import { normalizeCheckoutSession } from "./normalizeSession";
import { openCheckout, type CheckoutCallbacks } from "./openCheckout";

export type CreateCheckoutBody = {
  product_id: string;
  partner_code?: string;
  affiliatee_id?: string;
  /** Campaign code (the Black Friday LP sends "bf2026"); the server decides whether it still applies. */
  offer?: string;
  /** The Hebrew v2 pricing sends "he", so Stripe returns the buyer to /heb/purchase-complete. */
  lang?: "he";
};

export type CreateCheckoutCallbacks = CheckoutCallbacks & {
  purchaseTracking?: PendingPurchase;
  /** Which pricing surface opened this checkout, for funnel attribution. */
  analyticsLocation?: string;
  /** Plan name, so the funnel can be broken down by tier. */
  analyticsTier?: string;
};

/**
 * The single choke point every checkout on the site goes through — /pricing,
 * /pricing-heb, /pt-br/pricing and the LP offer cards all land here. Emitting
 * the funnel events from this one place is what keeps them consistent across
 * six surfaces.
 *
 * These events are funnel steps, not the record of money. Revenue is reconciled
 * from Stripe and Polar in the PostHog warehouse (`subscriptions_unified`); a
 * client-side event is blockable and must never become the source of truth for
 * revenue. They exist because a warehouse row cannot be a step in a PostHog
 * funnel, and because Stripe checkout sessions are not synced at all — so
 * without this, the drop between a pricing click and a payment is unmeasurable.
 *
 * Two audiences, two routes. `trackEvent` reaches PostHog only. The GA4
 * `add_to_cart` and `begin_checkout` below reach GA4 and, through the GTM server
 * container, Meta, Reddit and TikTok — the events the retargeting ad sets bid
 * on. Those used to be each caller's job, and four live surfaces (the geo
 * checker's in-report trial, the homepage pricing section, /lp/ai-ready and
 * /lp/black-friday) never did it, while /lp/plugin and /lp/heb-action sent the
 * cart and never the checkout. Firing them here is what makes "every checkout
 * goes through one place" true for the ad platforms as well. Callers must not
 * fire them again, or every step double-counts.
 *
 * Both need `purchaseTracking` - the line items and value are the same ones
 * /purchase-complete reports - so a caller that omits it gets no cart, no
 * checkout and, because the pending purchase is never stored, no purchase event
 * either. Always pass it.
 */
export async function createAndOpenCheckout(
  body: CreateCheckoutBody,
  callbacks?: CreateCheckoutCallbacks
): Promise<void> {
  const tracking = callbacks?.purchaseTracking;
  if (tracking) {
    storePendingPurchase(tracking);
    // Before the session request on purpose: picking a plan is the intent being
    // measured, and whether Stripe then opens is what CHECKOUT_FAILED answers.
    trackAddToCart(tracking.items, tracking.value, tracking.currency);
  }

  const analyticsBase = {
    product_id: body.product_id,
    tier: callbacks?.analyticsTier,
    price: callbacks?.purchaseTracking?.value,
    currency: callbacks?.purchaseTracking?.currency,
    location: callbacks?.analyticsLocation,
  };

  const res = await fetch("/api/checkout/create-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message =
      typeof data === "object" && data !== null && typeof data.error === "string"
        ? data.error
        : "checkout_unavailable";
    trackEvent(AnalyticsEvents.CHECKOUT_FAILED, {
      ...analyticsBase,
      error: message,
    });
    throw new Error(message);
  }
  const session = normalizeCheckoutSession(data);

  // Fired once the session exists but before the overlay mounts: a failure in
  // openCheckout below is a different, rarer problem and reports separately.
  trackEvent(AnalyticsEvents.CHECKOUT_STARTED, {
    ...analyticsBase,
    provider: session.provider,
  });

  // begin_checkout fires when the checkout itself has loaded, then hands over to
  // whatever the caller wanted on load.
  const onLoaded = tracking
    ? () => {
        trackBeginCheckout(tracking.items, tracking.value, tracking.currency);
        callbacks?.onLoaded?.();
      }
    : callbacks?.onLoaded;

  try {
    await openCheckout(session, { ...callbacks, onLoaded });
  } catch (err) {
    trackEvent(AnalyticsEvents.CHECKOUT_FAILED, {
      ...analyticsBase,
      provider: session.provider,
      error: err instanceof Error ? err.message : "checkout_open_failed",
    });
    throw err;
  }
}
